import { Injectable } from '@angular/core';
import { HttpInterceptorService } from '../http-interceptor/http-interceptor.service';
import { DialogService } from '../dialog/dialog.service';
import { GlobalFunctions } from '../global-functions/global-functions.service';
import { CommandRequestsService } from '../command-requests/command-requests.service';
import { UsersService } from '../users/users.service';
import { filter, interval, map, Observable, Subscription, switchMap, takeWhile, timer } from 'rxjs';
import { UnitType } from 'src/app/types/UnitType';
import { UserService } from 'src/app/lib';
import { TranslateService } from '@ngx-translate/core';
import { BoxConfig, BoxConfigChange } from '../../types/BoxConfig';
import { WhitelabelInstance } from '../whitelabel/whitelabel-instance.service';
import { snakeCase } from 'lodash';
import { ToastService } from '../../shared/services/toast/toast.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BoxResponsesComponent } from '../../components/command-responses/box-responses/box-responses.component';
import { ResetType } from '../../lib/commands';

@Injectable()
export class PointService {
  selectedPoint: any = null;

  constructor(
    private _http: HttpInterceptorService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private toastService: ToastService,
    private gf: GlobalFunctions,
    private cmdReq: CommandRequestsService,
    private usersService: UsersService,
    private userService: UserService,
    private whiteLabelInstance: WhitelabelInstance,
    private translate: TranslateService
  ) {}

  startAs(point): Promise<any> {
    return new Promise((resolve) => {
      this.usersService.selectCustomer().then((customer) => {
        if (customer) {
          this.start(point, customer.PK_CustomerID).then((res) => {
            resolve(res);
          });
        } else {
          resolve(false);
        }
      });
    });
  }
  startAsOwner(point): Promise<any> {
    if (!point.FK_DefaultCustomerID) return;

    return new Promise((resolve) => {
      this.start(point, point.FK_DefaultCustomerID).then((res) => {
        resolve(res);
      });
    });
  }
  startSelf(point): Promise<boolean> {
    return new Promise((resolve) => {
      this.userService.getUser().subscribe((user) => {
        this.start(point, user.PK_CustomerID).then((res) => {
          resolve(res);
        });
      });
    });
  }
  stop(boxID, sessionID): Promise<any> {
    return new Promise((resolve) => {
      this.dialogService
        .prompt(this.translate.instant('CONFIRM_STOP_CHARGING'), null, this.translate.instant('STOP_CHARGING'))
        .subscribe((accepted) => {
          if (accepted) {
            this._http.get('Commands/RemoteStop/' + boxID + '/' + sessionID).subscribe(
              (res) => {
                resolve(res);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
    });
  }
  forceStop(pointID): Promise<'stopped' | 'sent' | false> {
    return new Promise((resolve) => {
      //Be careful with this. Will
      this._http.get(`sessions/connector/${pointID}/force-end-session`).subscribe(
        (res) => {
          resolve('stopped');
        },
        (err) => {
          if (err.status === 204) {
            resolve('sent');
          } else {
            this.toastService.error(err);

            resolve(false);
          }
        }
      );
    });
  }
  getSessionByPoint(pointID): Promise<boolean> {
    return new Promise((resolve) => {
      this._http.get(`sessions/connector/${pointID}/active`).subscribe((res) => {
        resolve(res);
      });
    });
  }
  getOCPPLogs(boxID, dateFrom, dateTo): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('ChargingBoxes/LogOcppByDate/' + boxID + '/' + dateFrom + '/' + dateTo + '/true').subscribe(
        (res) => {
          //res.logs = res.logs.filter(item=>item.messageAction==='MeterValues').map(item=>item.message.meterValue[0]);
          resolve(res);
        },
        (err) => {
          resolve(false);
          this.toastService.error(err);
        }
      );
    });
  }
  getMeterValueLogs(pointID, dateFrom, dateTo): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.get('Metervalues/ByPointAndDate/' + pointID + '/' + dateFrom + '/' + dateTo + '/true').subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getConnectionLogs(boxID, dateFrom, dateTo): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('ChargingBoxes/GetConnectionsActivityByDate/' + boxID + '/' + dateFrom + '/' + dateTo).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          resolve(false);
          this.toastService.error(err);
        }
      );
    });
  }

  setAvailable(boxID, evseID, connectorID) {
    return this._http.post(`Commands/ChangeAvailability`, {
      IsOperative: true,
      ChargingBoxID: boxID,
      evseId: evseID,
      connectorID_OCPP20: connectorID
    });
  }

  forcePutBackInQ(sessionID): Promise<any> {
    return new Promise((resolve) => {
      this.dialogService
        .prompt(
          this.translate.instant('CONFIRM_USER_RETURN_TO_QUEUE'),
          this.translate.instant('USER_RETURN_TO_QUEUE_DESC')
        )
        .subscribe((doPut) => {
          if (doPut) {
            this._http.get('LoadBalancing/ForcePutBackInTheQueue/' + sessionID).subscribe((res) => {
              if (res.success) {
                resolve(true);
                this.toastService.success('RETURN_TO_QUEUE_SUCCESS');
              } else {
                resolve(false);
              }
            });
          } else {
            resolve(false);
          }
        });
    });
  }
  changeNamesToSMS(points: any[], index = 0): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!points || !points.length) return;
      points.forEach((point, index) => {
        point.Name = point.ChargerCode;
        this.saveGeneric(point).then(
          (success) => {
            if (index + 1 === points.length) {
              resolve(true);
            }
          },
          (err) => {
            reject(points[index]);
          }
        );
      });
    });
  }
  setSMSCodesASC(points, stationID) {
    const translated = this.translate.instant('PREFIX');
    this.dialogService.input(translated, translated, this.translate.instant('CHANGE_CODES')).subscribe((prefix) => {
      if (!prefix) return;
      let pointIDs = [];
      points = points.sort((a, b) => {
        return this.gf.sort(a.Name, b.Name);
      });
      points.forEach((point) => {
        pointIDs.push(point.PK_ChargePointID);
      });
      let smsChange = {
        Points: pointIDs,
        SMSPrefix: prefix.toUpperCase(),
        FK_ChargingStationID: parseInt(stationID)
      };
      this._http.post('SMS/ChangeSMSCodes', smsChange).subscribe(
        (res) => {
          this.toastService.success('SMS_CODES_CHANGED');
        },
        (err) => {
          this.toastService.error(err);
        }
      );
    });
  }

  changeStation(boxID, wallSettingID): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.put('ChargingBoxes/ChangeStationOfABox/' + boxID + '/' + wallSettingID, {}).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  private start(point, customerID): Promise<any> {
    return new Promise((resolve, reject) => {
      this.dialogService
        .prompt(this.translate.instant('CONFIRM_START_CHARGING'), null, this.translate.instant('START_CHARGING'))
        .subscribe((accepted) => {
          if (accepted) {
            point.State = 'Starting';

            this._http
              .post('Commands/RemoteStart', {
                FK_ChargingPointID: point.PK_ChargePointID,
                FK_CustomerID: customerID,
                Origin: 'Dashboard'
              })
              .subscribe(
                (res) => {
                  resolve(res);
                },
                (err) => {
                  this.toastService.error(err);
                  resolve(false);
                }
              );
          } else {
            resolve(false);
          }
        });
    });
  }

  addSlave(point): Promise<any> {
    return new Promise((resolve) => {
      //ChargePoints/AddChargePointToExistingBox/{boxId}/{userID}/{ChargingPointName}/{ParkingSpotName}/{hasRFID}/{hasPhysicalKey}
      this.userService.getUser().subscribe((user) => {
        const transaltedPointName = this.translate.instant('POINT_NAME');
        this.dialogService
          .input(
            transaltedPointName,
            transaltedPointName,
            null,
            `${this.translate.instant('MASTER_NAME')}: ${point.Name}`
          )
          .subscribe((pointName) => {
            if (pointName) {
              this._http
                .get(`ChargePoints/AddChargePointToExistingBox/${point.PK_ChargeBoxID}/${pointName}`)
                .subscribe({
                  next: (res) => {
                    this.toastService.success('SLAVE_ADDED');
                    resolve(true);
                  },
                  error: (err) => {
                    this.toastService.error(err);
                    resolve(false);
                  }
                });
            } else {
              resolve(false);
            }
          });
      });
    });
  }
  getDiagnostics(boxID, deviceID, startTime?, stopTime?, location?) {
    let request = {
      PK_ChargingBoxID: boxID,
      location: location,
      startTime: startTime,
      stopTime: stopTime,
      deviceID: deviceID
    };
    this._http.post('Commands/GetDiagnostics', request).subscribe((res) => {
      this.startWait(
        res,
        this.translate.instant('DIAGNOSTICS_RETRIEVED'),
        this.translate.instant('SOMETHING_WENT_WRONG'),
        this.translate.instant('GET_DIAGNOSTICS'),
        () => {
          this.waitForDiagnostics(res);
        }
      );
    });
  }
  private waitForDiagnostics(res) {
    this._http.get('Commands/GetDiagnosticsFiles/' + res.datas.Boxes[0].PK_CommandRequestBoxID).subscribe(
      (res) => {},
      (err) => {
        if (err.errorName === 'diagnosticsNotUploadedYet') {
          setTimeout(() => {
            this.waitForDiagnostics(res);
          }, 200);
        } else {
          this.toastService.error(err);
        }
      }
    );
  }
  getPoint(id, reload?): Promise<any> {
    return new Promise((resolve) => {
      if (this.selectedPoint && !reload) {
        resolve(this.selectedPoint);
      } else {
        this._http.get('ChargePoints/' + id + '/true').subscribe(
          (point) => {
            if (point) this.selectedPoint = point;
            resolve(point);
          },
          (err) => {
            resolve(null);
          }
        );
      }
    });
  }
  selectPoint(point) {
    this.selectedPoint = point;
  }
  modifyState(point) {
    if (point.LastHeartbeat) {
      if (point.State !== 'Offline') {
        //point.CurrentStatus ==='UpdatingFirmware' && point.State !=='Offline'
        point.StateStyle = point.CurrentStatusPoint;
        point.FilterState = point.CurrentStatusPoint;
        point.ViewState =
          point?.CurrentStatusPoint && this.translate.instant(String(point.CurrentStatusPoint).toUpperCase());
        switch (point.CurrentStatusPoint) {
          case 'Available':
            point.FilterState = point.State; //point.CurrentStatusPoint;
            // point.ViewState = this.translate.instant(point.State); //point.CurrentStatusPoint;

            if (point.ChargingSessionID) {
              //Charging in our system but not in box
              point.Warning = this.translate.instant('WARNING_BOX_AVAILABLE_TRANSACTION_ONGING');
            }
            break;
          case 'Preparing':
            //Check charging
            //Charging in our system but not in box = show warning
            if (point.ChargingSessionID) {
              point.Warning = this.translate.instant('WARNING_BOX_PREPARING_TRANSACTION_ONGING');
            }
            // point.ViewState = this.translate.instant('PREPARING');
            point.StatusTip = this.translate.instant('BOX_PREPARING_DESCRIPTION');
            break;
          case 'Charging':
          case 'Occupied':
            //Discharging, Occupied, Charging, charging with warning
            if (!point.ChargingSessionID) {
              //Warning
              point.Warning = this.translate.instant('WARNING_BOX_CHARGING_BUT_NO_TRANSACTION');
            }
            //We use system status in this case
            point.FilterState = point.State;
            point.ViewState = this.translate.instant(snakeCase(point.State).toUpperCase());
            point.StateStyle = point.State;
            break;
          case 'ChargingWithoutSessionSecondChance':
          case 'ChargingWithoutSession':
            point.StateStyle = 'Charging';
            point.FilterState = 'Charging';
            point.ViewState = this.translate.instant('IS_CHARGING');
            point.Warning = this.translate.instant('WARNING_BOX_CHARGING_BUT_NO_TRANSACTION');
            break;
          case 'ChargingOnly':
            point.Warning = this.translate.instant('WARNING_BOX_CHARGING_BUT_NO_TRANSACTION');
            //We use system status in this case
            point.FilterState = point.State;
            // point.ViewState = point.State;
            break;
          case 'ChargingOnlySecond':
            point.Warning = this.translate.instant('WARNING_BOX_SEND_METER_VALUES_NO_TRANSACTION');
            //We use system status in this case
            point.FilterState = point.State;
            // point.ViewState = point.State;
            break;
          case 'SuspendedEV':
            if (point.State === 'Suspended') {
              // point.ViewState = 'Paused';
              point.FilterState = 'Suspended';
            } else {
              // point.ViewState = 'Paused by car';
              point.StatusTip = this.translate.instant('PAUSED_BY_CAR_HINT');
            }
            break;
          case 'SuspendedEVSE':
            if (point.State === 'Suspended') {
              // point.ViewState = 'Paused';
              point.FilterState = 'Suspended';
            } else {
              // point.ViewState = 'Paused by box';
            }

            break;
          case 'Finishing':
            //Check charging
            if (point.ChargingSessionID) {
              point.Warning = this.translate.instant('WARNING_BOX_FINISHING_TRANSACTION_ONGING');
            }
            // point.ViewState = 'Finishing';
            point.StatusTip = this.translate.instant('FINISHING_DESCRIPTION');
            break;
          case 'Reserved':
            // point.ViewState = 'Reserved';
            break;
          case 'Unavailable':
            if (point.CurrentStatus == 'UpdatingFirmware') {
              // point.ViewState = 'Updating';
              point.FilterState = 'Updating';
              point.StateStyle = point.CurrentStatus;
            } else {
              // point.ViewState = 'Online';
              point.FilterState = 'Online';
            }
            point.Unavailable = true;
            break;
          case 'Faulted':
            // point.ViewState = 'Faulted';
            //Check if charging/transaction ongoing
            break;
          case 'UpdatingFirmware':
            // point.ViewState = 'Updating';
            break;
          default:
            //In case we don't have the status yet, or it is something unknown
            // point.ViewState = point.State;
            point.StateStyle = point.State;
            point.FilterState = point.State;
            break;
        }
      } else {
        point.ViewState = this.translate.instant('OFFLINE');
        point.StateStyle = 'Offline';
        point.FilterState = 'Offline';
        //Check if charging/transaction ongoing
      }
    } else {
      //Never been online
      point.ViewState = this.translate.instant('UNINSTALLED');
      point.StateStyle = 'Uninstalled';
      point.FilterState = 'Uninstalled';
    }
    if (point.PointInstallationStatus === 'Installed') {
      //Show icon beeing tested by our system
    }
  }
  restart(point): Promise<any> {
    return new Promise((resolve?) => {
      this.dialogService
        .prompt(this.translate.instant('CONFIRM_RESTART'), null, this.translate.instant('RESTART'))
        .subscribe((doRestart) => {
          if (doRestart) {
            this._http.get('Commands/Reset/' + point.PK_ChargeBoxID + '/1').subscribe({
              next: (res) => {
                resolve(res);
              },
              error: (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            });
          } else {
            resolve(false);
          }
        });
    });
  }
  forceRestart(point): Promise<any> {
    return new Promise((resolve?) => {
      this.dialogService
        .prompt(
          this.translate.instant('FORCE_RESTART'),
          this.translate.instant('CONFIRM_FORCE_RESTART'),
          this.translate.instant('RESTART')
        )
        .subscribe((doRestart) => {
          if (doRestart) {
            this._http.post(`Commands/Reset/${point.PK_ChargeBoxID}`, { ResetType: ResetType.Immediate }).subscribe({
              next: (res) => {
                resolve(res);
              },
              error: (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            });
          } else {
            resolve(false);
          }
        });
    });
  }
  unlockPoint(point): Promise<any> {
    return new Promise((resolve?) => {
      this.dialogService
        .prompt(this.translate.instant('CONFIRM_UNLOCK_CHARGING_CABLE'), null, this.translate.instant('UNLOCK'))
        .subscribe((doUnlock) => {
          if (doUnlock) {
            this._http.get('Commands/UnlockConnector/' + point.PK_ChargeBoxID + '/' + point.EvseNumber).subscribe(
              (res) => {
                resolve(res);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
    });
  }
  clearCache(point): Promise<any> {
    return new Promise((resolve?) => {
      if (!point || !point.PK_ChargeBoxID) return;

      this.dialogService
        .prompt(this.translate.instant('CONFIRM_CLEAR_CACHE'), null, this.translate.instant('CLEAR_CACHE'))
        .subscribe((doClear) => {
          if (doClear) {
            this._http.get('Commands/ClearCache/' + point.PK_ChargeBoxID).subscribe(
              (res) => {
                resolve(res);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
    });
  }
  clearChargingProfile(point): Promise<any> {
    return new Promise((resolve?) => {
      if (!point || !point.PK_ChargeBoxID) return;

      this.dialogService
        .prompt(this.translate.instant('CONFIRM_CLEAR_CHARGING_PROFILE'), null, this.translate.instant('CLEAR_PROFILE'))
        .subscribe((doClear) => {
          if (doClear) {
            this._http.get('Commands/ClearChargingProfiles/' + point.PK_ChargeBoxID).subscribe(
              (res) => {
                resolve(res);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
    });
  }
  update(point): Promise<any> {
    return new Promise((resolve?) => {
      if (!point || !point.PK_ChargeBoxID) return;
      let prompt = this.translate.instant('CONFIRM_UPDATE_LATEST_VERSION');
      let content = this.translate.instant('FROM_COMMA_TO', {
        from: point.FirmwareVersion,
        to: point.LatestFirmwareVersion
      });

      this.dialogService.prompt(prompt, content, this.translate.instant('UPDATE')).subscribe((doUpdate) => {
        if (doUpdate) {
          this._http.get('Commands/UpdateToLatestFirmware/' + point.PK_ChargeBoxID).subscribe(
            (res) => {
              resolve(res);
            },
            (err) => {
              this.toastService.error(err);
              resolve(false);
            }
          );
        } else {
          resolve(false);
        }
      });
    });
  }

  setConfig(config: BoxConfigChange): Promise<any> {
    return new Promise((resolve?) => {
      this._http.post('Commands/SetConfig', config).subscribe(
        (res) => {
          resolve(res.datas);
        },
        (err) => {
          resolve({
            error: err.errorName,
            config: config,
            device: config.BoxID
          });
        }
      );
    });
  }

  setAuthentication(boxID, auth: boolean): Promise<any> {
    return new Promise((resolve?) => {
      this._http.get('Commands/SetDefaultAuthentication/' + boxID + '/' + auth).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          this.toastService.error(err);
          resolve(false);
        }
      );
    });
  }

  setAutoCharge(boxID, autoCharge: boolean): Promise<any> {
    return new Promise((resolve?) => {
      this._http.get('Commands/SetAutoCharge/' + boxID + '/' + autoCharge).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          this.toastService.error(err);
          resolve(false);
        }
      );
    });
  }

  checkSupport(action: OCPPFeature, ocppv: OCPPVersions, point) {
    if (!point.ExtraInformation) return true; //Will allow the command to be sent if no info is known
    return point.ExtraInformation[ocppv + '_' + action];
  }
  permanentlyLock(boxID, locked: boolean): Promise<any> {
    return new Promise((resolve?) => {
      this._http.get('Commands/SetDefaultPermanentCableLocking/' + boxID + '/' + locked).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          if (locked) {
            this.toastService.error(err);
          } else {
            this.toastService.error(err);
          }
          resolve(false);
        }
      );
    });
  }
  checkIfCharging(point): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.get('Commands/CheckIfThePointIsCharging/' + point.PK_ChargePointID).subscribe(
        (res) => {
          const hasFailed = res.Boxes.some((box) => box.HasFailed);

          if (hasFailed) {
            resolve(false);
          } else {
            resolve(true);
          }
        },
        (err) => {
          this.toastService.error(err);
          reject();
        }
      );
    });
  }
  setDefaultConfig(point): Promise<any> {
    return new Promise((resolve?) => {
      if (!point || !point.PK_ChargeBoxID) return;

      this.dialogService
        .prompt(
          this.translate.instant('CONFIRM_SET_DEFAULT_CONFIG'),
          null,
          this.translate.instant('SET_DEFAULT_CONFIG')
        )
        .subscribe((doConfig) => {
          if (doConfig) {
            this._http.get('Commands/SetDefaultConfig/' + point.PK_ChargeBoxID).subscribe(
              (res) => {
                resolve(res);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
    });
  }
  getConfiguration(point): Promise<any> {
    return new Promise((resolve?, reject?) => {
      this._http.get('Commands/GetConfiguration/' + point.PK_ChargeBoxID).subscribe(
        (configuration) => {
          resolve(configuration);
        },
        (err) => {
          this.toastService.error();
          reject(err);
        }
      );
    });
  }
  getCachedConfiguration(boxID: string, refreshIfMissing = true): Observable<BoxConfig> {
    return this._http.getNonJson(`ChargingBoxes/GetConfig/${boxID}`, { observe: 'response' }).pipe(
      switchMap((response) => {
        if (response?.status === 204 && refreshIfMissing) {
          return this.refreshConfigurationCache(boxID);
        }

        response.body.Cached = true;

        return [response];
      }),
      map((response) => (response.body ? response.body : (response as BoxConfig)))
    );
  }
  refreshConfigurationCache(boxID: string, lastRetrieved?: number): Observable<BoxConfig> {
    return this._http.get(`Commands/GetConfiguration/${boxID}`).pipe(
      switchMap(() =>
        timer(0, 1000).pipe(
          switchMap(() =>
            this._http.getNonJson(`ChargingBoxes/GetConfig/${boxID}`, {
              observe: 'response'
            })
          ),
          takeWhile(
            (res) => res.status === 204 || (lastRetrieved && Date.parse(res.body.Retrieved) <= lastRetrieved),
            true
          ),
          filter((res) => res.status !== 204 && (!lastRetrieved || Date.parse(res.body.Retrieved) > lastRetrieved)),
          map((res) => res.body)
        )
      )
    );
  }
  getChargingBox(chargingBoxId: number): Observable<any> {
    return this._http.get(`ChargingBoxes/GetBox/${chargingBoxId}`);
  }
  getChargingBoxModel(boxModelId: number): Observable<any> {
    return this._http.get(`ChargingBoxModels/${boxModelId}`);
  }
  getDefaultConfiguration(boxID): Promise<any> {
    return new Promise((resolve?) => {
      this._http.get('Commands/GetExpectedConfiguration/' + boxID).subscribe((configuration) => {
        resolve(configuration.datas);
      });
    });
  }
  toggleMaintenance(point) {
    const confirm = point.IsMaintenance
      ? this.translate.instant('CONFIRM_MAINTENANCE_OFF')
      : this.translate.instant('CONFIRM_MAINTENANCE_ON');
    const buttonAction = point.IsMaintenance
      ? this.translate.instant('TURN_OFF_MAINTENANCE')
      : this.translate.instant('TURN_ON_MAINTENANCE');
    const successMessage = point.IsMaintenance
      ? this.translate.instant('MAINTENANCE_TURNED_OFF')
      : this.translate.instant('MAINTENANCE_TURNED_ON');

    this.dialogService.prompt(confirm, null, buttonAction).subscribe((doToggle) => {
      if (doToggle) {
        let state = {
          PK_ChargingBoxID: point.PK_ChargeBoxID,
          newState: !point.IsMaintenance
        };
        this._http.put('ChargingBoxes/PutMaintenanceState', state).subscribe(
          (res) => {
            point.IsMaintenance = !point.IsMaintenance;
            this.toastService.success(successMessage);
          },
          (err) => {
            this.toastService.error(err);
          }
        );
      }
    });
  }
  toggleActivation(point) {
    this.dialogService
      .prompt(
        point.IsActive
          ? this.translate.instant('CONFIRM_DEACTIVATE_POINT')
          : this.translate.instant('CONFIRM_ACTIVATE_POINT'),
        point.IsActive ? this.translate.instant('DEACTIVATE_CHARGER_HELP_TEXT') : null,
        point.IsActive ? this.translate.instant('DEACTIVATE') : this.translate.instant('ACTIVATE')
      )
      .subscribe((doToggle) => {
        if (doToggle) {
          let pointToPut = Object.assign({}, point);
          pointToPut.IsActive = !point.IsActive;

          //Must check if the state is something custom, state is also updated in point..
          if (!['Online', 'Occupied', 'Charging', 'Suspended', 'Offline'].includes(point.State))
            pointToPut.State = 'Offline';

          this._http.put('ChargePoints', pointToPut).subscribe(
            (res) => {
              this.toastService.success(point.IsActive ? 'POINT_DEACTIVATED' : 'POINT_ACTIVATED');
              point.IsActive = !point.IsActive;
            },
            (err) => {
              this.toastService.error(err);
            }
          );
        }
      });
  }
  getConnectivity(boxID, duration: any = 1): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('ChargingBoxes/GetConnectionsActivity/' + boxID + '/' + duration).subscribe((res) => {
        resolve(res);
      });
    });
  }
  addSeveralOwners(points): Promise<any> {
    return new Promise((resolve) => {
      this.dialogService.addOwner(true, true, true).subscribe((ownerObject) => {
        if (ownerObject) {
          let notAdded = '';
          this.toastService.success('ADDING_MULTIPLE_OWNERS');
          let doAddSeveral = (i = 0, points, auth, subscr, semiregister) => {
            if (i >= points.length) {
              //Done
              if (notAdded) {
                this.dialogService.alert(this.translate.instant('SOMETHING_WENT_WRONG'), null, notAdded);
              }
              this.toastService.success('DONE_ADDING_OWNERS');
              resolve(true);
            } else {
              let obj = {
                Email: points[i].NewOwnerEmail,
                FK_ChargePointID: points[i].PK_ChargePointID,
                FK_ServicePricingFactorID: 0,
                ValidationKEY: null,
                IsCustomerPayingForSubscription: subscr,
                SemiRegisterIfCustomerNotFound: semiregister,
                appToken: this.whiteLabelInstance.whitelabelID
              };
              this._http.post('ChargePoints/ConnectCustomerToPoint', obj).subscribe(
                (res) => {
                  if (points[i].AuthenticationEnabled !== auth) {
                    //Update authentication

                    this.setAuthentication(points[i].PK_ChargeBoxID, auth).then((res) => {
                      //Wait until this is done or else we might update the view before it is changed
                      i++;
                      doAddSeveral(i, points, auth, subscr, semiregister);
                    });
                  } else {
                    i++;
                    doAddSeveral(i, points, auth, subscr, semiregister);
                  }
                },
                (err) => {
                  //For each error we make an error string to show later
                  console.log(err);
                  notAdded += `${this.translate.instant('ERROR')}: ${err.errorName} (${points[i].DeviceID})\n\n`;
                  i++;
                  doAddSeveral(i, points, auth, subscr, semiregister);
                }
              );
            }
          };

          doAddSeveral(
            0,
            points,
            ownerObject.authentication,
            ownerObject.IsCustomerPayingForSubscription,
            ownerObject.SemiRegisterIfCustomerNotFound
          );
        } else {
          resolve(false);
        }
      });
    });
  }
  setPointPrice(pointID, pricingFactor): Promise<any> {
    return new Promise((resolve) => {
      this._http
        .post('ServicePricingFactors/ChangeCustomPricingClaimedPoint/' + pointID, pricingFactor)
        .subscribe((res) => {
          resolve(res);
        });
    });
  }
  addOwner(
    pointID,
    boxID,
    originalAuth,
    pricingFactor?: number,
    isSubscriptionPaidByCustomers: boolean = true
  ): Promise<any> {
    return new Promise((resolve) => {
      if (pointID) {
        this.dialogService.addOwner(originalAuth, isSubscriptionPaidByCustomers).subscribe((ownerObject) => {
          if (ownerObject) {
            let obj = {
              Email: ownerObject.email,
              FK_ChargePointID: pointID,
              FK_ServicePricingFactorID: pricingFactor || 0,
              ValidationKEY: null,
              IsCustomerPayingForSubscription: ownerObject.IsCustomerPayingForSubscription,
              SemiRegisterIfCustomerNotFound: ownerObject.SemiRegisterIfCustomerNotFound,
              appToken: this.whiteLabelInstance.whitelabelID
            };
            this._http.post('ChargePoints/ConnectCustomerToPoint', obj).subscribe(
              (res) => {
                //FIX: Check response
                this.toastService.success('OWNER_ADDED');

                if (originalAuth !== ownerObject.authentication) {
                  //Update authentication
                  this.updateAuthentication(ownerObject.authentication, boxID);
                }
                resolve(ownerObject.email);
              },
              (err) => {
                if (err.errorName === 'notEnoughPrivileges') {
                  this.toastService.error('PERMISSION_DENIED_NO_FUCNTION_ACCESS');
                } else {
                  this.toastService.error(err);
                }

                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
      }
    });
  }
  updateAuthentication(authentication, boxID?, box?) {
    const errorMessage = this.translate.instant('SOMETHING_WENT_WRONG');
    const buttonText = authentication
      ? this.translate.instant('DISABLE_AUTHENTICATION')
      : this.translate.instant('ENABLE_AUTHENTICATION');

    this.setAuthentication(boxID, authentication).then((res) => {
      this.startWait(res, null, errorMessage, buttonText);
    });
  }

  saveGeneric(point): Promise<any> {
    return new Promise((resolve) => {
      this._http.put('ChargePoints', point).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          this.toastService.error(err);
          resolve(false);
        }
      );
    });
  }
  removeOwner(pointID): Promise<any> {
    return new Promise((resolve) => {
      if (!pointID) return;
      this.dialogService.removeOwner().subscribe((data) => {
        if (data) {
          this._http.get(`ChargePoints/RemoveDefaultCustomer/${pointID}/${data.deactiveCharger}`).subscribe({
            next: (res) => {
              resolve(true);
              this.toastService.success('OWNER_REMOVED');
            },
            error: (err) => {
              this.toastService.error(err);
              resolve(false);
            }
          });
        } else {
          resolve(false);
        }
      });
    });
  }
  deletePoint(pointID): Promise<any> {
    return new Promise((resolve) => {
      //ChargePoints/{point_id}/{deleteAssociatedBox}
      this._http.delete('ChargePoints/' + pointID).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          this.toastService.error(err);
          resolve(false);
        }
      );
    });
  }

  getPoints(filters?: PointFilters): Promise<any> {
    return new Promise((resolve) => {
      this._http.post('ChargingBoxes/GetBoxesByFilters', filters).subscribe(
        (points) => {
          if (points.datas) {
            resolve(points.datas);
          } else {
            resolve([]);
          }
        },
        (err) => {
          this.toastService.error(err);
          resolve([]);
        }
      );
    });
  }

  getBox(boxID): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.get('ChargingBoxes/GetBoxFull/' + boxID).subscribe(
        (res) => {
          if (res.datas) {
            resolve(res.datas);
          } else {
            reject(this.translate.instant('SOMETHING_WENT_WRONG'));
          }
        },
        (err) => {
          reject(
            this.translate.instant('SOMETHING_WENT_WRONG_WITH_ERROR', {
              errorName: err.errorName
            })
          );
        }
      );
    });
  }
  updateBox(box): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.put('ChargingBoxes', box).subscribe(
        (res) => {
          if (res.success) {
            resolve(box);
          } else {
            reject(this.translate.instant('SOMETHING_WENT_WRONG'));
          }
        },
        (err) => {
          reject(
            this.translate.instant('SOMETHING_WENT_WRONG_WITH_ERROR', {
              errorName: err.errorName
            })
          );
        }
      );
    });
  }
  createPriceObject(
    kWhCost: number,
    timeCost: number,
    startupCost: number,
    occupiedCost: number,
    stationID,
    currency,
    countryID,
    timeUnit: UnitType = 'PerMinute',
    occupiedUnit: UnitType = 'PerMinute'
  ): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('Countries/ByID/' + countryID).subscribe(
        (country) => {
          this._http.get(`products/location/${stationID}/charging-product`).subscribe(
            (service) => {
              var pricing = {
                FK_ServiceID: service.PK_ServiceID,
                Name: 'Single point',
                CostPrice: kWhCost,
                CostPriceVATPercentage: country.VatPercentageCharging,
                Cost_Charging_Time: timeCost,
                FixedStartupCost: startupCost,
                FixedStartupCostVATPercentage: country.VatPercentageStandard,
                Currency: currency,
                Keys: this.gf.guid4(),
                AmpsMax_DefaultPrice: 0,
                SpeedMaxAmps: 0,
                Coefficient_Price_MaxAmps: 0,
                Type_Cost_Charging_Time: timeUnit,
                Type_Cost_Occupied: occupiedUnit,
                Cost_Occupied: occupiedCost,
                Cost_Occupied_VATPercentage: country.VatPercentageStandard,
                Type_Cost_Charging: 'PerkWh'
              };
              resolve(pricing);
            },
            (err) => {
              resolve(false);
            }
          );
        },
        (err) => {
          resolve(false);
        }
      );
    });
  }
  createPrice(
    kWhCost: number,
    timeCost: number,
    startupCost: number,
    occupiedCost: number,
    stationID,
    currency,
    countryID,
    timeUnit: UnitType = 'PerMinute',
    occupiedUnit: UnitType = 'PerMinute'
  ): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('Countries/ByID/' + countryID).subscribe(
        (country) => {
          this._http.get(`products/location/${stationID}/charging-product`).subscribe(
            (service) => {
              var pricing = {
                FK_ServiceID: service.PK_ServiceID,
                Name: 'Single point',
                CostPrice: kWhCost,
                CostPriceVATPercentage: country.VatPercentageCharging,
                Cost_Charging_Time: timeCost,
                FixedStartupCost: startupCost,
                FixedStartupCostVATPercentage: country.VatPercentageStandard,
                Currency: currency,
                Keys: this.gf.guid4(),
                AmpsMax_DefaultPrice: 0,
                SpeedMaxAmps: 0,
                Coefficient_Price_MaxAmps: 0,
                Type_Cost_Charging_Time: timeUnit,
                Type_Cost_Occupied: occupiedUnit,
                Cost_Occupied: occupiedCost,
                Cost_Occupied_VATPercentage: country.VatPercentageStandard,
                Type_Cost_Charging: 'PerkWh'
              };

              this._http.post('ServicePricingFactors', pricing).subscribe(
                (response) => {
                  resolve(response.datas);
                },
                (err) => {
                  resolve(false);
                }
              );
            },
            (err) => {
              resolve(false);
            }
          );
        },
        (err) => {
          resolve(false);
        }
      );
    });
  }
  startWait(res, message, errorMessage, functionName, callback?, failedCallback?): Subscription {
    //Will send empty response if error in the http-request or if user aborts when prompted
    if (!res || !res.datas || !res.datas.PK_CommandRequestID) return;

    const intervalSeconds = 2; //Check response interval
    const timeout = 10; //How long it will check before timeout

    const checkInterval = interval(intervalSeconds * 1000);
    const subscribe: Subscription = checkInterval.subscribe((i) => {
      //Check response by interval
      this.checkResponse(res, message, errorMessage, callback, failedCallback).then((done) => {
        if (done) {
          subscribe.unsubscribe();
        } //else pending
      });

      const seconds = i * intervalSeconds;
      if (seconds >= timeout) {
        //Timeout
        this.toastService.error(
          this.translate.instant('TIMEOUT_FUNCTION_SEE_COMMAND_RESPONSES', {
            functionName: functionName
          })
        );
        subscribe.unsubscribe();
      }
    });
    return subscribe;
  }
  checkResponse(response, message, errorMessage, callback?, failedCallback?): Promise<any> {
    return new Promise((resolve) => {
      this.cmdReq.checkStatus(response.datas.PK_CommandRequestID).then(
        (success) => {
          if (success) {
            if (message) {
              this.toastService.success(message);
            }
            if (callback) {
              callback();
            }
            resolve(true);
          } else {
            //else pending
            resolve(false);
          }
        },
        (err) => {
          if (typeof err === 'string') {
            this.toastService.error(err);
          } else if (typeof err === 'number') {
            this.toastService
              .info('REQUESTS_FAILED', 'SHOW_RESPONSES')
              .onAction()
              .subscribe(() => {
                this.showRequestDetails(err);
              });
          }

          if (failedCallback) {
            failedCallback();
          }
          resolve(true);
        }
      );
    });
  }
  showRequestDetails(requestID: number) {
    const dialogRef: MatDialogRef<BoxResponsesComponent> = this.dialog.open(BoxResponsesComponent, {
      autoFocus: false,
      width: '80vw'
    });
    dialogRef.componentRef.setInput('requestID', requestID);
  }
  checkIfDatas(res) {
    if (res && res.datas) {
      return res.datas;
    } else if (res && !res.datas) {
      return res;
    } else {
      return null;
    }
  }

  updatePassword(request): Observable<void> {
    return this._http.put('ChargingBoxes/update-password', request);
  }

  clear() {
    this.selectedPoint = null;
  }
}

export class PointFilters {
  stations?: number[] = [];
  boxModelsWanted?: number[] = [];
  hasAuthentication?: boolean = null;
  ocppVersions?: string[] = [];
  firmwareVersionNotIncluded?: string[] = [];
  firmwareVersionIncluded?: string[] = [];
  isCharging?: boolean = null;
  hasWrongConfiguration?: boolean = null;
  WallSettingsIds: number[] = [];
}

export type OCPPFeature =
  | 'canChangeAuthentication'
  | 'canPermanentlyLockCable'
  | 'supportsChargingProfile'
  | 'supportsFirmwareManagement'
  | 'supportsTriggerMessages'
  | 'supportsUnlockConnector';
export const OCPPFeatures: OCPPFeature[] = [
  'canChangeAuthentication',
  'canPermanentlyLockCable',
  'supportsChargingProfile',
  'supportsFirmwareManagement',
  'supportsTriggerMessages',
  'supportsUnlockConnector'
];

export type OCPPVersions = 'j16';
